import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Input,
  Segment,
  Header,
  Icon,
  Loader,
  Message,
  Label,
  Dropdown,
} from "semantic-ui-react";
import { SG_GET_LLM_USAGE_LOGS } from "../constants/actions";
import { format } from "date-fns";

const statusOptions = [
  { key: "all", value: "", text: "All Status" },
  { key: "success", value: "true", text: "Success" },
  { key: "error", value: "false", text: "Error" },
];

export default function UsageLogList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: "",
    success: "",
  });

  const {
    llm_usage_logs = [],
    llm_usage_logs_pending = false,
    llm_usage_logs_error,
    llm_usage_logs_total = 0,
  } = useSelector((state) => state.llm_log || {});

  useEffect(() => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.success) params.success = filters.success;

    dispatch({
      type: SG_GET_LLM_USAGE_LOGS,
      payload: { queryParams: params },
    });
  }, [dispatch, filters.search, filters.success]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm:ss");
    } catch {
      return dateString;
    }
  };

  return (
    <div>
      <Segment>
        <Header as="h2">
          <Icon name="list" />
          <Header.Content>
            LLM Usage Logs
            <Header.Subheader>Track all LLM API calls and usage</Header.Subheader>
          </Header.Content>
        </Header>

        <div style={{ marginBottom: "20px", display: "flex", gap: "10px" }}>
          <Input
            icon="search"
            placeholder="Search by model, user, or session..."
            value={filters.search}
            onChange={(e) =>
              setFilters({ ...filters, search: e.target.value })
            }
            style={{ flex: 1 }}
          />
          <Dropdown
            selection
            options={statusOptions}
            value={filters.success}
            onChange={(e, { value }) =>
              setFilters({ ...filters, success: value })
            }
            placeholder="Filter by status"
          />
        </div>

        {llm_usage_logs_error && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{llm_usage_logs_error}</p>
          </Message>
        )}

        {llm_usage_logs_pending ? (
          <Loader active inline="centered">
            Loading...
          </Loader>
        ) : (
          <>
            <Table celled striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Timestamp</Table.HeaderCell>
                  <Table.HeaderCell>Model Config</Table.HeaderCell>
                  <Table.HeaderCell>Organization/Employee</Table.HeaderCell>
                  <Table.HeaderCell>Tokens</Table.HeaderCell>
                  <Table.HeaderCell>Cost (μUSD)</Table.HeaderCell>
                  <Table.HeaderCell>Latency</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {llm_usage_logs.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan="8" textAlign="center">
                      <Icon name="inbox" size="large" />
                      <p>No usage logs found</p>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  llm_usage_logs.map((log) => (
                    <Table.Row key={log.id}>
                      <Table.Cell>
                        {formatDate(log.created_at)}
                      </Table.Cell>
                      <Table.Cell>
                        {log.model_config ? (
                          <>
                            <strong>{log.model_config.model_name || "N/A"}</strong>
                            <br />
                            <small style={{ color: "#666" }}>
                              {log.model_config.provider || "Unknown"}
                            </small>
                          </>
                        ) : (
                          <span style={{ color: "#999" }}>No config</span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {log.organization ? (
                          <div>
                            <Icon name="building" />
                            Org #{log.organization}
                          </div>
                        ) : null}
                        {log.employee ? (
                          <div>
                            <Icon name="user" />
                            Emp #{log.employee}
                          </div>
                        ) : null}
                        {!log.organization && !log.employee && "N/A"}
                      </Table.Cell>
                      <Table.Cell>
                        <div>Prompt: {log.prompt_tokens?.toLocaleString() || 0}</div>
                        <div>Completion: {log.completion_tokens?.toLocaleString() || 0}</div>
                        <div>
                          <strong>
                            Total: {log.total_tokens?.toLocaleString() || 0}
                          </strong>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        {log.cost_micro_usd?.toLocaleString() || 0} μUSD
                        <br />
                        <small style={{ color: "#666" }}>
                          ${((log.cost_micro_usd || 0) / 1000000).toFixed(6)}
                        </small>
                      </Table.Cell>
                      <Table.Cell>
                        {log.latency_ms ? `${log.latency_ms}ms` : "N/A"}
                      </Table.Cell>
                      <Table.Cell>
                        <Label 
                          color={log.success ? "green" : "red"} 
                          size="small"
                        >
                          {log.success ? "Success" : "Error"}
                        </Label>
                        {log.status_code && (
                          <div style={{ marginTop: 4 }}>
                            <small>Code: {log.status_code}</small>
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          icon
                          size="small"
                          onClick={() =>
                            navigate(`/app/llm-usage/usage-logs/${log.id}`)
                          }
                        >
                          <Icon name="eye" />
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table>

            <div style={{ marginTop: "10px", color: "#666" }}>
              Total: {llm_usage_logs_total} log(s)
            </div>
          </>
        )}
      </Segment>
    </div>
  );
}